import React, { createContext, useReducer, useEffect, useRef } from 'react';
import Logger from 'services/Logger';
import { AuditLogFacetGroups, AuditLogOptions } from 'store/audit/types';
import { Action } from 'modules/types/common';
import { FiltersFormValues, FiltersFormContext } from '../types/filter';
import { filterValues, optionValues } from './utils';
import { filters } from '.';

//
//  Constant: defaultValues
//  Description: The default values used for the filters form
//

export const defaultValues = {
  filters: {},
  endsAt: '',
  startsAt: '',
  facets: {
    [AuditLogFacetGroups.categories]: [] as any[],
    [AuditLogFacetGroups.actions]: [] as any[],
    [AuditLogFacetGroups.persons]: [] as any[],
    [AuditLogFacetGroups.dateAndTimeOfEvents]: [] as any[],
    [AuditLogFacetGroups.performedBy]: [] as any[]
  }
} as FiltersFormValues;

export const FiltersContext = createContext<FiltersFormContext>({
  resetForm: () => {},
  setFieldValue: (_0: string, _1: any) => {},
  onChange: (_: React.ChangeEvent<any>) => {},
  onClear: () => {},
  onSubmit: (_?: React.FormEvent<any>) => {},
  values: defaultValues,
  filters
});

interface State {
  values: FiltersFormValues;
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        values: {
          ...state.values,
          ...action.payload
        }
      };
    case 'RESET_FORM':
      return {
        ...state,
        values: action.payload
      };
    default:
      return state;
  }
}

//
//  Component: AuditLogFiltersForm
//
//  Description:
//  <AuditLogFiltersForm /> is a component that controls the filters state.
//  Provider Search Filters values should all be controlled in here as a single source of truth for form values.
//
//  Props:
//  initialOptions: AuditLogOptions
//  onSubmit: (options: AuditLogOptions) => void;
//  onClear: (options: AuditLogOptions) => void;
//  children: React.ReactNode | (props: FiltersFormContext) => React.ReactNode;
//
export function AuditLogFiltersForm({
  initialOptions = {
    filters: [],
    startsAt: '',
    endsAt: ''
  },
  onSubmit: handleSubmit,
  onClear: handleClear,
  children
}: {
  initialOptions: AuditLogOptions;
  onSubmit: (options: AuditLogOptions) => void;
  onClear: (options: AuditLogOptions) => void;
  children: ((props: FiltersFormContext) => React.ReactNode) | React.ReactNode;
}) {
  const initialValues = filterValues(initialOptions);

  const [state, set] = useReducer(reducer, {
    values: initialValues
  });

  const mounted = useRef(false);

  const resetForm = (values: Partial<FiltersFormValues> = {}) => {
    set({
      type: 'RESET_FORM',
      payload: { ...initialValues, ...values }
    });
  };

  const setFieldValue = (field: string, value: any): void => {
    if (field in state.values) {
      set({
        type: 'SET_FIELD_VALUE',
        payload: { [field]: value }
      });
    } else {
      Logger.error('AuditLogFiltersForm:', `Invalid field "${field}"`);
    }
  };

  const onChange = (event: React.ChangeEvent<any>): void => {
    event.persist();
    setFieldValue(event.target.name, event.target.value);
  };

  const onClear = () => {
    const options = optionValues(initialOptions, defaultValues);
    handleClear(options);
  };

  const onSubmit = (event?: React.FormEvent<any>) => {
    const options = optionValues(initialOptions, state.values);
    event?.preventDefault();
    handleSubmit(options);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (mounted.current) {
      resetForm();
    }
  }, [initialOptions]);

  const props = {
    resetForm,
    setFieldValue,
    onChange,
    onClear,
    onSubmit,
    filters,
    ...state
  };

  return (
    <FiltersContext.Provider value={props}>
      {typeof children === 'function' ? children(props) : children}
    </FiltersContext.Provider>
  );
}
